import { z } from "zod";
import {
  AlarmStateSchema,
  ConcurrencyMetricSchema,
  EventTypeSchema,
  JourneyDimensionSchema,
  JourneyEdgeSchema,
  SeveritySchema,
} from "./event-envelope.js";
import {
  AlarmCaseIndexRecordSchema,
  EnvironmentRecordSchema,
  EnvironmentRoleSchema,
  QuotaIncreaseRequestRecordSchema,
  QuotaIncreaseStatusSchema,
  ServiceQuotaSnapshotRecordSchema,
  SupportCaseRecordSchema,
} from "./records.js";

/**
 * Portal API contract (CLAUDE.md sections 11-12). Shared with the
 * frontend so the two cannot drift. Every list view that is scoped to a
 * customer resolves that customer from the JWT (role `customer`) or the
 * path (role `internal-team`); the response shapes are identical.
 */

export const RoleSchema = z.enum(["internal-team", "customer"]);
export type Role = z.infer<typeof RoleSchema>;

export const DayWindowSchema = z.union([z.literal(7), z.literal(30), z.literal(60), z.literal(90)]);
export type DayWindow = z.infer<typeof DayWindowSchema>;

/** How Gairm staff look at a customer's portal: as the customer would see it, or able to change it as the customer's manager would. */
export const ViewAsModeSchema = z.enum(["read", "write"]);
export type ViewAsMode = z.infer<typeof ViewAsModeSchema>;

/**
 * Requirements section 13.1. Non-sensitive, per user, stored on
 * `PortalUsers.preferences` and returned on `/v1/me`. Every timestamp
 * stays UTC in the tables; time zone and date format are presentation.
 */
export const ThemePreferenceSchema = z.enum(["light", "dark", "system"]);
export const DateFormatPreferenceSchema = z.enum(["auto", "iso", "us", "intl"]);
export const MotionPreferenceSchema = z.enum(["standard", "reduced", "auto"]);
/**
 * A nudge is a banner asking the user to finish something (enrol a passkey,
 * name a second manager). "later" hides it until the next sign-in - `at` is
 * compared with the token's auth_time; "never" hides it for good. Kept with
 * the preferences so it follows the user between devices.
 */
export const NudgeStateSchema = z.object({ state: z.enum(["later", "never"]), at: z.string() });
export type NudgeState = z.infer<typeof NudgeStateSchema>;
export const NudgeIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/);
const preferenceFields = {
  theme: ThemePreferenceSchema,
  /** An IANA zone, or "auto" for whatever the device reports each session. */
  timeZone: z.string().min(1).max(64),
  dateFormat: DateFormatPreferenceSchema,
  /** A portal path; the UI offers the role-appropriate list and falls back to "/" if it is no longer reachable. */
  landingPage: z.string().regex(/^\/[A-Za-z0-9\-/]*$/).max(128),
  motion: MotionPreferenceSchema,
  nudges: z.record(NudgeIdSchema, NudgeStateSchema),
};
export const UserPreferencesSchema = z.object({
  theme: preferenceFields.theme.default("system"),
  timeZone: preferenceFields.timeZone.default("auto"),
  dateFormat: preferenceFields.dateFormat.default("auto"),
  landingPage: preferenceFields.landingPage.default("/"),
  motion: preferenceFields.motion.default("auto"),
  nudges: preferenceFields.nudges.default({}),
});
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;
/** A patch: only the keys sent. Built without defaults on purpose - zod applies a default under partial() too, which would overwrite what is stored. */
export const UserPreferencesUpdateSchema = z.object(preferenceFields).partial();
export type UserPreferencesUpdate = z.infer<typeof UserPreferencesUpdateSchema>;

export const MeResponseSchema = z.object({
  subject: z.string(),
  email: z.string().optional(),
  role: RoleSchema,
  /** ADR-0009: the organisation, not an environment. Per-environment roles are resolved per request. */
  organisationId: z.string().optional(),
  /** The API's version (root package.json); the UI shows it beside its own so a stale bundle is visible. */
  portalVersion: z.string().optional(),
  /** Set when a Gairm staff member is viewing this organisation's portal (ADR-0011): the identity is still theirs. */
  viewAs: z
    .object({
      organisationId: z.string(),
      organisationName: z.string().optional(),
      mode: ViewAsModeSchema,
    })
    .optional(),
  /** The caller's own (section 13.1); defaults applied, so always complete when present. */
  preferences: UserPreferencesSchema.optional(),
  /** Section 13.2: what the header shows instead of the bare email, when set - `firstName lastName`, or the provider's name for a federated user. */
  displayName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  /** ADR-0014: set for a federated user - the provider name they signed in through. Account hides password, MFA and passkeys for them; their name is the provider's. */
  identityProvider: z.string().optional(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Section 13.3 [17]: the last 90 days of sign-ins from Cognito, and the user's own security-relevant audit rows. */
export interface SecurityActivityResponse {
  signIns: Array<{
    id: string;
    at: string;
    type: string;
    outcome: "pass" | "fail" | "in_progress" | "unknown";
    ip?: string;
    device?: string;
    place?: string;
    risk?: string;
  }>;
  changes: Array<{ id: string; at: string; action: string; result: string; actor: string; viaGairm: boolean }>;
  /** Cognito's advanced-security history is a Plus-plan feature; absent means this deployment cannot show it. */
  signInsAvailable: boolean;
}

export const SecurityReportSchema = z.object({ eventId: z.string().min(1).max(200), note: z.string().max(1000).optional() });

const NamePartSchema = z.string().trim().max(40);
/** Section 13.2: first and last name; the display name is derived from them. Both empty clears the name and the header falls back to the email. */
export const ProfileUpdateSchema = z.object({ firstName: NamePartSchema.default(""), lastName: NamePartSchema.default("") });
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

/** `First Last`, or whichever half exists; empty when neither does. */
export function displayNameFrom(firstName: string | undefined, lastName: string | undefined): string {
  return [firstName, lastName].map((s) => s?.trim() ?? "").filter(Boolean).join(" ");
}

/**
 * Req 30 (#67) — where the caller holds access, and as what.
 *
 * A role is held per environment, never across the organisation
 * (ADR-0008/0009), and per-environment roles are resolved server-side
 * per request rather than carried as claims. So the UI cannot derive
 * this from the token; it has to ask.
 *
 * `scope` distinguishes the two shapes of answer. An `internal-team`
 * caller bypasses membership entirely (`requireManager` returns early
 * for them), so listing `Memberships` for them would be both wrong and
 * empty — the honest answer is "everything, by virtue of the group".
 */
export const MyMembershipSchema = z.object({
  environmentId: z.string(),
  friendlyName: z.string().optional(),
  awsAccountNumber: z.string().optional(),
  role: EnvironmentRoleSchema,
  /** Section 13.4: who granted it and when, from the membership row. */
  grantedBy: z.string().optional(),
  grantedAt: z.string().optional(),
});
export type MyMembership = z.infer<typeof MyMembershipSchema>;

export const MyMembershipsResponseSchema = z.object({
  scope: z.enum(["membership", "internal-team"]),
  organisationId: z.string().optional(),
  memberships: z.array(MyMembershipSchema),
});
export type MyMembershipsResponse = z.infer<typeof MyMembershipsResponseSchema>;

/** One row of the Customers / Trusted Advisor list views (design.md "MVP views"). */
export const EnvironmentSummarySchema = z.object({
  environmentId: z.string(),
  friendlyName: z.string(),
  awsAccountNumber: z.string(),
  status: EnvironmentRecordSchema.shape.status,
  onboardingStatus: EnvironmentRecordSchema.shape.onboarding_status,
  connectivityVerifiedAt: z.string().optional(),
  lastHeartbeatAt: z.string().optional(),
  templateVersion: z.string().optional(),
  forwarderDlqDepth: z.number().int().optional(),
  /** Count of events of the requested source in the requested window. */
  eventCount: z.number().int().nonnegative(),
  lastEventAt: z.string().optional(),
  openCriticalAlarms: z.number().int().nonnegative(),
});
export type EnvironmentSummary = z.infer<typeof EnvironmentSummarySchema>;

export const EnvironmentsListResponseSchema = z.object({
  window: DayWindowSchema,
  source: EventTypeSchema,
  currentTemplateVersion: z.string(),
  customers: z.array(EnvironmentSummarySchema),
});
export type EnvironmentsListResponse = z.infer<typeof EnvironmentsListResponseSchema>;

export const EnvironmentDetailResponseSchema = z.object({
  customer: EnvironmentRecordSchema.omit({ api_key_id: true, api_key_secret_arn: true, destination_config: true }),
  currentTemplateVersion: z.string(),
  enabledComponents: z.array(z.string()),
  /** False once the poller reported the support plan lacks Trusted Advisor (requirements.md section 5). */
  trustedAdvisorAvailable: z.boolean(),
});
export type EnvironmentDetailResponse = z.infer<typeof EnvironmentDetailResponseSchema>;

export const EventSummarySchema = z.object({
  eventId: z.string(),
  eventSource: EventTypeSchema,
  timestamp: z.string(),
  severity: SeveritySchema.optional(),
  /** Alarm name / TA check name - the one-line label for a list. */
  title: z.string(),
  state: AlarmStateSchema.optional(),
  resource: z.string().optional(),
  reason: z.string().optional(),
});
export type EventSummary = z.infer<typeof EventSummarySchema>;

export const EventsListResponseSchema = z.object({
  events: z.array(EventSummarySchema),
  nextCursor: z.string().optional(),
});
export type EventsListResponse = z.infer<typeof EventsListResponseSchema>;

export const AlarmsListResponseSchema = z.object({
  alarms: z.array(
    AlarmCaseIndexRecordSchema.omit({ open_event_sk: true, last_event_sk: true, external_ticket_ref: true }).extend({
      hasTicket: z.boolean(),
    }),
  ),
});
export type AlarmsListResponse = z.infer<typeof AlarmsListResponseSchema>;

export const QuotasListResponseSchema = z.object({
  quotas: z.array(ServiceQuotaSnapshotRecordSchema.omit({ sk: true, near_limit: true })),
  /** Whether this customer opted into QuotaIncreaseRequestRole (ADR-0002). */
  increaseRequestsAvailable: z.boolean(),
});
export type QuotasListResponse = z.infer<typeof QuotasListResponseSchema>;

export const SupportCasesListResponseSchema = z.object({
  cases: z.array(SupportCaseRecordSchema),
  /** False when the last poll reported the support plan does not include the API. */
  available: z.boolean(),
});
export type SupportCasesListResponse = z.infer<typeof SupportCasesListResponseSchema>;

export const ConcurrencyResponseSchema = z.object({
  hours: z.number().int().positive(),
  series: z.array(
    z.object({
      metric: ConcurrencyMetricSchema,
      points: z.array(z.object({ t: z.string(), v: z.number() })),
    }),
  ),
});
export type ConcurrencyResponse = z.infer<typeof ConcurrencyResponseSchema>;

/** design.md "Focus page". `accountsGoneQuiet` is absent for the customer role (inherently cross-customer). */
export const FocusResponseSchema = z.object({
  tiles: z.object({
    criticalAlarmsOpen: z.number().int().nonnegative(),
    quotasNearLimit: z.number().int().nonnegative(),
    accountsGoneQuiet: z.number().int().nonnegative().optional(),
    openSupportCases: z.number().int().nonnegative(),
  }),
  criticalAlarms: z.array(
    z.object({
      environmentId: z.string(),
      friendlyName: z.string(),
      alarmName: z.string(),
      alarmArn: z.string(),
      openedAt: z.string(),
      lastSeenAt: z.string(),
      occurrenceCount: z.number().int(),
      hasTicket: z.boolean(),
    }),
  ),
  quotasNearLimit: z.array(
    z.object({
      environmentId: z.string(),
      friendlyName: z.string(),
      serviceCode: z.string(),
      quotaCode: z.string(),
      quotaName: z.string(),
      value: z.number(),
      usage: z.number(),
      usagePercent: z.number(),
      adjustable: z.boolean(),
      increaseRequestsAvailable: z.boolean(),
      /** Set when an increase request for this quota is already open with AWS - one per quota is all AWS allows. */
      openRequestStatus: QuotaIncreaseStatusSchema.optional(),
    }),
  ),
  accountsGoneQuiet: z
    .array(
      z.object({
        environmentId: z.string(),
        friendlyName: z.string(),
        lastHeartbeatAt: z.string().optional(),
        connectivityVerifiedAt: z.string().optional(),
        reason: z.enum(["never_connected", "no_heartbeat", "heartbeat_stale"]),
      }),
    )
    .optional(),
  generatedAt: z.string(),
});
export type FocusResponse = z.infer<typeof FocusResponseSchema>;

export const QuotaIncreaseRequestInputSchema = z.object({
  serviceCode: z.string().min(1),
  quotaCode: z.string().min(1),
  desiredValue: z.number().positive(),
});
export type QuotaIncreaseRequestInput = z.infer<typeof QuotaIncreaseRequestInputSchema>;

export const QuotaIncreaseRequestsResponseSchema = z.object({
  requests: z.array(QuotaIncreaseRequestRecordSchema.omit({ sk: true })),
});
export type QuotaIncreaseRequestsResponse = z.infer<typeof QuotaIncreaseRequestsResponseSchema>;

/**
 * What AWS said in the seconds after the request was filed: some quotas
 * are approved on the spot, others open a Support case that can take
 * days, and a few are still pending when we stop waiting.
 */
export const QuotaIncreaseRequestOutcomeSchema = z.object({
  ok: z.literal(true),
  awsRequestId: z.string().min(1),
  status: QuotaIncreaseStatusSchema,
  caseId: z.string().optional(),
  /** The limit AWS now reports, when the request was approved while we waited. */
  appliedValue: z.number().optional(),
});
export type QuotaIncreaseRequestOutcome = z.infer<typeof QuotaIncreaseRequestOutcomeSchema>;

// ---- Journeys (ADR-0003) ----

export const JourneyPathsResponseSchema = z.object({
  windowStart: z.string(),
  windowEnd: z.string(),
  dimension: JourneyDimensionSchema,
  slice: z.string(),
  availableSlices: z.partialRecord(JourneyDimensionSchema, z.array(z.string())),
  contactCount: z.number().int().nonnegative(),
  edges: z.array(JourneyEdgeSchema),
});
export type JourneyPathsResponse = z.infer<typeof JourneyPathsResponseSchema>;

export const ContactCandidateSchema = z.object({
  contactId: z.string(),
  initiationTimestamp: z.string(),
  disconnectTimestamp: z.string().optional(),
  channel: z.string().optional(),
  initiationMethod: z.string().optional(),
  queueName: z.string().optional(),
  customerEndpoint: z.string().optional(),
  systemEndpoint: z.string().optional(),
  agentConnected: z.boolean(),
});
export type ContactCandidate = z.infer<typeof ContactCandidateSchema>;

export const ContactSearchResponseSchema = z.object({
  candidates: z.array(ContactCandidateSchema),
  truncated: z.boolean(),
});
export type ContactSearchResponse = z.infer<typeof ContactSearchResponseSchema>;

const PanelStatusSchema = z.enum(["ok", "not_available", "error"]);

export const FlowStepSchema = z.object({
  timestamp: z.string(),
  flowName: z.string().optional(),
  moduleType: z.string(),
  identifier: z.string().optional(),
  dwellMs: z.number().nonnegative().optional(),
  error: z.boolean(),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

export const CallJourneySchema = z.object({
  contactId: z.string(),
  status: z.enum(["assembling", "complete", "failed"]),
  assembledAt: z.string().optional(),
  error: z.string().optional(),
  legs: z.array(
    z.object({
      contactId: z.string(),
      initiationTimestamp: z.string().optional(),
      disconnectTimestamp: z.string().optional(),
      initiationMethod: z.string().optional(),
      channel: z.string().optional(),
      queueName: z.string().optional(),
      agentId: z.string().optional(),
      customerEndpoint: z.string().optional(),
      systemEndpoint: z.string().optional(),
      previousContactId: z.string().optional(),
      disconnectReason: z.string().optional(),
    }),
  ),
  timeline: z.object({ status: PanelStatusSchema, steps: z.array(FlowStepSchema), note: z.string().optional() }),
  lambdas: z.object({
    status: PanelStatusSchema,
    invocations: z.array(
      z.object({
        functionName: z.string(),
        timestamp: z.string(),
        durationMs: z.number().optional(),
        logLines: z.array(z.string()),
      }),
    ),
    note: z.string().optional(),
  }),
  bot: z.object({
    status: PanelStatusSchema,
    turns: z.array(z.object({ timestamp: z.string(), intent: z.string().optional(), utterance: z.string().optional(), slots: z.record(z.string(), z.string()).optional(), fallback: z.boolean() })),
    note: z.string().optional(),
  }),
  agent: z.object({
    status: PanelStatusSchema,
    transcript: z.array(z.object({ participant: z.string(), beginOffsetMs: z.number(), content: z.string(), sentiment: z.string().optional() })),
    categories: z.array(z.string()),
    note: z.string().optional(),
  }),
  dataDips: z.object({ status: PanelStatusSchema, items: z.array(z.object({ functionName: z.string(), request: z.unknown(), response: z.unknown() })), note: z.string().optional() }),
  recording: z.object({ status: PanelStatusSchema, url: z.string().optional(), expiresAt: z.string().optional(), note: z.string().optional() }),
});
export type CallJourney = z.infer<typeof CallJourneySchema>;
export type FlowStep = z.infer<typeof FlowStepSchema>;
